import { BindingEngine } from 'aurelia-binding';
import { autoinject } from 'aurelia-dependency-injection';
import { Subscription } from 'aurelia-event-aggregator';
import { RegexValidation } from "./base-validations/regex-validation";
import { CompareValidation } from "./base-validations/compare-validation";
import { DateValidation } from "./base-validations/date-validation";
import { RulesHolder, ValidationTypeEnum, ValidationActivation, ValidationRule } from "./validations.models";
import { CustomValidationRenderer } from "./custom-validation-renderer";
import {
    ControllerValidateResult,
    validateTrigger,
    ValidationController,
    ValidationRules,
    ValidationRenderer,
    ValidateInstruction
} from "aurelia-validation";
import { Reflection } from '../utilities/reflection';
import { EmailValidator } from '../utilities/email-validator';

@autoinject
export class EntityValidationsService {

    private dateValidator: DateValidation = new DateValidation();
    private compareValidator: CompareValidation = new CompareValidation();
    private regexValidation: RegexValidation = new RegexValidation();
    private customRenderer: CustomValidationRenderer;

    public constructor(private validationController: ValidationController, private bindingEngine: BindingEngine) {
        this.customRenderer = new CustomValidationRenderer();
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.validationController.validateTrigger = validateTrigger.manual;
    }

    public changeValidationRenderer(validationRenderer: ValidationRenderer) {
        this.validationController.removeRenderer(this.customRenderer);
        this.validationController.removeRenderer(validationRenderer);
        this.validationController.addRenderer(validationRenderer);
    }

    public getValidator(entity: any, rulesHolder: RulesHolder): ClientSideValidator {
        const subscriptions: Subscription[] = [];
        this.injectValidationRulesInEntity(rulesHolder, entity, subscriptions);
        const validator = new ClientSideValidator(this.validationController, subscriptions);
        return validator;
    }

    public getValidatorForArray(arrayOfEntities: any[], rulesHolder: RulesHolder): ClientSideValidator {
        const subscriptions: Subscription[] = [];
        arrayOfEntities.forEach(x => {
            this.injectValidationRulesInEntity(rulesHolder, x, subscriptions);
        });
        const validator = new ClientSideValidator(this.validationController, subscriptions);
        return validator;
    }

    public validateEntity(): Promise<ControllerValidateResult> {
        return this.validationController.validate();
    }

    evaluateIfEmptyValue(obj: any, property: string) {
        const value = obj[property];
        return value === null || value === '' || value === undefined;
    }

    evaluateActivation(obj: any, rulesHolder: RulesHolder): boolean {
        switch (rulesHolder.activation) {
            case ValidationActivation.Always: {
                return true;
            }
            case ValidationActivation.OnlyWhenAllFieldFilled: {
                return !rulesHolder.rules.some(x => !obj[x.property]);
            }
            case ValidationActivation.OnlyWhenAnyFieldFilled: {
                return rulesHolder.rules.some(x => obj[x.property]) || obj.id > 0;
            }
        }
    }

    public injectValidationRulesInEntity(rulesHolder: RulesHolder, item: any, subscriptions: any[]) {
        if (rulesHolder === null || rulesHolder === undefined) { return null; }
        let ruleValidation: any;
        for (let i: number = 0; i < rulesHolder.rules.length; i++) {
            const rule = rulesHolder.rules[i];
            if (Object.keys(rule).length === 0 && rule.constructor === Object) {
                continue;
            }
            const keys = Object.keys(item);
            //if there is a difference in casing between property from validation and the one in the entity to validate then take the casing from the entity to validate.
            const path = keys.filter(x => x.toLowerCase() == rule.property.toLowerCase())[0] || rule.property;

            ruleValidation = ruleValidation === undefined ? ValidationRules.ensure(path) : ruleValidation.ensure(path);

            let parameters = null;
            if (typeof (rule.parameters) == "string") {
                try {
                    parameters = JSON.parse(rule.parameters)
                } catch {
                    parameters = {};
                }
            } else {
                parameters = rule.parameters;
            }

            switch (rule.validationType) {
                case ValidationTypeEnum.Required: {
                    ruleValidation = ruleValidation
                        .satisfies(value => {
                            if (!rule.parameters || this.isRequiredFromConditional(rule, item)) {
                                return value !== null
                                    && value !== undefined
                                    && !(typeof value == "string" && !/\S/.test(value))
                            }
                            return true;
                        })
                        .when(x => this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder));
                }
                    break;
                case ValidationTypeEnum.NumericRange:
                    ruleValidation = ruleValidation
                        .satisfies(x => {
                            if (parameters.maximum && parameters.minimum) {
                                return x === null || Number(x) >= Number(parameters.minimum)
                                    && Number(x) <= Number(parameters.maximum)
                            }
                            else if (parameters.minimum) {
                                return x === null || Number(x) >= Number(parameters.minimum);
                            }
                            else if (parameters.maximum) {
                                return x === null || Number(x) <= Number(parameters.maximum)
                            }

                        })
                        .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                        .withMessage(rule.errorMessage);
                    break;
                case ValidationTypeEnum.MaxLength:
                    ruleValidation = ruleValidation
                        .satisfies(
                            x => {
                                const response = x === null || Number(x.toString().length) <= Number(parameters.length);
                                return response;
                            })
                        .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                        .withMessage(rule.errorMessage);
                    break;
                case ValidationTypeEnum.MinLength:
                    ruleValidation = ruleValidation
                        .satisfies(x => {
                            const response = x === null || Number(x.toString().length) >= Number(parameters.length);
                            return response;
                        })
                        .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                        .withMessage(rule.errorMessage);
                    break;
                case ValidationTypeEnum.RegularExpression:
                    {
                        const regex = new RegExp(parameters.pattern);
                        ruleValidation = ruleValidation
                            .satisfies(x => x === null || x === undefined || x === "" || this.regexValidation.validate(regex, x.toString()))
                            .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                            .withMessage(rule.errorMessage);
                    }
                    break;
                case ValidationTypeEnum.Email:
                    {
                        ruleValidation = ruleValidation
                            .satisfies(x => x === null || x === undefined || x === "" || EmailValidator.isValidEmail(x))
                            .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                            .withMessage(rule.errorMessage);
                    }
                    break;
                case ValidationTypeEnum.DateRange:
                    {
                        ruleValidation = ruleValidation
                            .satisfies((x) => x === null || x === undefined || x === "" || this.dateValidator.validate(parameters, x))
                            .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                            .withMessage(rule.errorMessage);
                    }
                    break;
                case ValidationTypeEnum.Compare:
                    {
                        ruleValidation = ruleValidation
                            .satisfies((x, o) => this.compareValidator.validate(parameters, o))
                            .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                            .withMessage(rule.errorMessage);
                    }
                    break;
                case ValidationTypeEnum.EitherOrRequired:
                    {
                        const otherPropertyAccessor = parameters.otherProperty;
                        const propertyAccessor = parameters.property;
                        ruleValidation = ruleValidation
                            .satisfies((x, o) => !!o[otherPropertyAccessor] || !!o[propertyAccessor])
                            .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                            .withMessage(rule.errorMessage);
                    }
                    break;
                case ValidationTypeEnum.Logic:
                    {
                        ruleValidation = ruleValidation
                            .satisfies((x) => {
                                return x === parameters.expectedValue;
                            })
                            .when(x => !this.evaluateIfEmptyValue(x, path) && this.evaluateActivation(x, rulesHolder))
                            .withMessage(rule.errorMessage);
                    }
                    break;
                default:
                    break;
            }
            ruleValidation = rule.errorMessage ? ruleValidation.withMessage(rule.errorMessage) : ruleValidation;
        }

        this.applyNestedValidations(rulesHolder, item, subscriptions);

        if (item) {

            if (rulesHolder.isCollection) {
                item.forEach(element => {
                    if (ruleValidation) {
                        element["__rules__"] = [];
                        ruleValidation.on(element);
                    }
                });
                const subscription = this.bindingEngine.collectionObserver(item).subscribe(changedValues => {
                    const countNew: number = changedValues[0].addedCount;
                    const startIndex: number = changedValues[0].index;
                    const addedValues: any[] = (item).slice(startIndex, startIndex + countNew);
                    addedValues.forEach(x => {
                        if (ruleValidation) {
                            x["__rules__"] = [];
                            ruleValidation.on(x);
                        }
                    });

                    this.applyNestedValidations(rulesHolder, addedValues, subscriptions);
                });
                subscriptions.push(subscription);
            } else if (ruleValidation) {
                item["__rules__"] = [];
                ruleValidation.on(item);
            }
        }

        return ruleValidation;
    }

    private applyNestedValidations(rulesHolder: RulesHolder, item: any, subscriptions: any[]) {
        for (let j: number = 0; j < rulesHolder.nestedRulesHolder.length; j++) {
            if (rulesHolder.isCollection && item) {
                item.forEach(element => {
                    if (element[rulesHolder.nestedRulesHolder[j].entityName]) {
                        this.injectValidationRulesInEntity(rulesHolder.nestedRulesHolder[j], element[rulesHolder.nestedRulesHolder[j].entityName], subscriptions);
                    }
                    this.injectValidationRulesInEntity(rulesHolder.nestedRulesHolder[j], element[rulesHolder.nestedRulesHolder[j].entityName], subscriptions);
                });
            }
            else {
                if (item && item[rulesHolder.nestedRulesHolder[j].entityName]) {
                    this.injectValidationRulesInEntity(rulesHolder.nestedRulesHolder[j], item[rulesHolder.nestedRulesHolder[j].entityName], subscriptions);
                }
            }
        }
    }

    private isRequiredFromConditional(rule: ValidationRule, item: any): boolean {

        const individualRules = rule.parameters.split("|");
        for (const parameterRule of individualRules) {
            let anyTrue = false;
            const conditionalPropertyRules = parameterRule.split('~');
            for (const conditionalPropertyRule of conditionalPropertyRules) {
                const strings = conditionalPropertyRule.split(':');
                const expectedProperty = Reflection.camelCase(strings[0]);

                const currentValue = item[expectedProperty] != null ? item[expectedProperty] : "null";
                if (strings.length == 2) {
                    const expectedValues = strings[1].split(',');
                    if (expectedValues.some(s => s.toLowerCase() == currentValue.toString().toLowerCase())) {
                        //if USPerson:True then ignore validation 
                        anyTrue = true;
                    }
                }
                else if (currentValue.toString() != "null") {
                    anyTrue = true;
                }
            }

            if (!anyTrue) {
                return null;
            }
        }
        return true;
    }
}

export class ClientSideValidator {
    private disposed: boolean = false;

    constructor(private validationController: ValidationController, private subscriptions: any[]) {
    }

    validate(instruction?: ValidateInstruction): Promise<ControllerValidateResult> {
        if (this.disposed) {
            throw new Error("Instance was disposed and cannot be reused");
        } else {
            return this.validationController.validate(instruction);
        }
    }

    public addError(message: string, obj: any, property: string) {
        this.validationController.addError(message, obj, property);
    }

    public reset() {
        this.validationController.reset();
    }

    public get isDisposed() {
        return this.disposed;
    }

    dispose() {
        if (this.subscriptions) {
            this.subscriptions.forEach(subscription => {
                if (subscription) {
                    subscription.dispose();
                }
            });
        }
        this.disposed = true;
    }
}
